import React, { useState, useEffect, useCallback } from 'react';
import BoxForm from './components/BoxForm';
import BoxKarte from './components/BoxKarte';
import { buildBoxGroups } from './boxGroups';
import LoginPage from './components/LoginPage';
import LabelPrint from './components/LabelPrint';
import SingleLabelPrint from './components/SingleLabelPrint';
import LocationPrint from './components/LocationPrint';
import LocationAssign from './components/LocationAssign';
import PasskeySettings from './components/PasskeySettings';
import { fetchBoxen, fetchKategorien, logout } from './api';
import {
  clearPendingLocation,
  loadPendingLocation,
  locationBaseFromPath,
  savePendingLocation,
  suggestLevel,
} from './locationFlow';

function isPreparedBox(box) {
  return !box.parent_box && !box.kategorie && !box.inhalt_stichworte &&
    !box.standort && !box.erinnerung_am && !box.erinnerung_text &&
    (!box.fotos || box.fotos.length === 0);
}

function localDateKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function reminderDateKey(value) {
  return String(value || '').slice(0, 10);
}

function formatReminderDate(value) {
  const dateKey = reminderDateKey(value);
  return dateKey
    ? new Intl.DateTimeFormat('de-DE').format(new Date(`${dateKey}T12:00:00`))
    : '';
}

function boxNumberFromPath() {
  const match = window.location.pathname.match(/^\/box\/([^/]+)\/?$/i);
  return match ? decodeURIComponent(match[1]).toUpperCase() : '';
}

export default function App() {
  const [setupMode, setSetupMode] = useState(() => window.location.pathname === '/zugang' && Boolean(window.location.hash));
  const [authenticated, setAuthenticated] = useState(null); // null = noch prüfen
  const [boxen, setBoxen] = useState([]);
  const [kategorien, setKategorien] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showLabelPrint, setShowLabelPrint] = useState(false);
  const [singleLabel, setSingleLabel] = useState(null);
  const [showLocationPrint, setShowLocationPrint] = useState(false);
  const [showPasskeySettings, setShowPasskeySettings] = useState(false);
  const [locationAssignment, setLocationAssignment] = useState(null);
  const [showPrepared, setShowPrepared] = useState(false);
  const [editBox, setEditBox] = useState(null);
  const [newBoxNumber, setNewBoxNumber] = useState('');
  const [filterKat, setFilterKat] = useState('');
  const [search, setSearch] = useState('');
  const [directLinkHandled, setDirectLinkHandled] = useState(false);
  const [directLocationHandled, setDirectLocationHandled] = useState(false);
  const requestedBoxNumber = boxNumberFromPath();
  const requestedLocationBase = locationBaseFromPath();

  useEffect(() => {
    fetch('/api/auth/check')
      .then(r => r.json())
      .then(d => setAuthenticated(window.location.pathname === '/zugang' ? false : d.authenticated))
      .catch(() => setAuthenticated(false));
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [b, k] = await Promise.all([fetchBoxen(), fetchKategorien()]);
      setBoxen(b);
      setKategorien(k);
    } catch (err) {
      if (err.message === '401') { setAuthenticated(false); return; }
      setError('Fehler beim Laden: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (authenticated) loadData(); }, [authenticated, loadData]);

  useEffect(() => {
    if (!authenticated || loading || directLinkHandled || !requestedBoxNumber) return;
    const pendingLocation = loadPendingLocation();
    if (pendingLocation) {
      setLocationAssignment({
        baseCode: pendingLocation.baseCode,
        level: pendingLocation.level,
        boxNumber: requestedBoxNumber,
      });
      setDirectLinkHandled(true);
      return;
    }
    const requestedBox = boxen.find(
      box => box.box_nummer.toUpperCase() === requestedBoxNumber
    );
    if (requestedBox) {
      setEditBox(requestedBox);
      setNewBoxNumber('');
      setShowForm(true);
    } else {
      setEditBox(null);
      setNewBoxNumber(requestedBoxNumber);
      setShowForm(true);
      setError('');
    }
    setDirectLinkHandled(true);
  }, [authenticated, loading, directLinkHandled, requestedBoxNumber, boxen]);

  useEffect(() => {
    if (!authenticated || loading || directLocationHandled || !requestedLocationBase) return;
    const level = suggestLevel(boxen, requestedLocationBase);
    savePendingLocation(requestedLocationBase, level);
    setLocationAssignment({ baseCode: requestedLocationBase, level, boxNumber: '' });
    setDirectLocationHandled(true);
  }, [authenticated, loading, directLocationHandled, requestedLocationBase, boxen]);

  async function handleLogout() {
    await logout();
    setAuthenticated(false);
    setBoxen([]);
    setKategorien([]);
  }

  if (authenticated === null) return null; // kurz warten während Auth-Check
  if (!authenticated || setupMode) return <LoginPage onLogin={() => { setSetupMode(false); setLoading(true); setAuthenticated(true); }} />;

  function handleSaved(saved) {
    setBoxen(prev => {
      const exists = prev.find(b => b.id === saved.id);
      const next = exists ? prev.map(b => b.id === saved.id ? saved : b) : [...prev, saved];
      return next.sort((a, b) => a.box_nummer.localeCompare(b.box_nummer, undefined, { numeric: true, sensitivity: 'base' }));
    });
    if (saved.kategorie && !kategorien.includes(saved.kategorie)) {
      setKategorien(prev => [...prev, saved.kategorie].sort());
    }
    // Formular bleibt offen – Nutzer schliesst manuell über "Schliessen"
  }

  function handleDeleted(id) {
    setBoxen(prev => prev.filter(b => b.id !== id));
  }

  function handleEdit(box) {
    setEditBox(box);
    setNewBoxNumber('');
    setShowForm(true);
  }

  function handleNewBox() {
    setEditBox(null);
    setNewBoxNumber('');
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditBox(null);
    setNewBoxNumber('');
    if (boxNumberFromPath()) window.history.replaceState({}, '', '/');
  }

  function closeLocationAssignment() {
    clearPendingLocation();
    setLocationAssignment(null);
    if (boxNumberFromPath() || locationBaseFromPath()) window.history.replaceState({}, '', '/');
  }

  // Filtern & Suchen
  const preparedBoxes = boxen.filter(isPreparedBox);
  const activeBoxes = boxen.filter(box => !isPreparedBox(box));
  const searchLower = search.trim().toLowerCase();
  const sourceBoxes = showPrepared || searchLower ? boxen : activeBoxes;
  const filtered = sourceBoxes.filter(b => {
    const katMatch = !filterKat || b.kategorie === filterKat;
    const searchMatch = !searchLower ||
      b.box_nummer.toLowerCase().includes(searchLower) ||
      (b.kategorie || '').toLowerCase().includes(searchLower) ||
      (b.inhalt_stichworte || '').toLowerCase().includes(searchLower) ||
      (b.standort || '').toLowerCase().includes(searchLower) ||
      (b.erinnerung_text || '').toLowerCase().includes(searchLower) ||
      reminderDateKey(b.erinnerung_am).includes(searchLower);
    return katMatch && searchMatch;
  });

  const groups = buildBoxGroups(filtered, boxen);
  const today = localDateKey();
  const dueReminders = boxen
    .filter(box => {
      const dateKey = reminderDateKey(box.erinnerung_am);
      return dateKey && dateKey <= today;
    })
    .sort((a, b) => reminderDateKey(a.erinnerung_am).localeCompare(reminderDateKey(b.erinnerung_am)));

  return (
    <div className="lager-app">
      <a className="skip-link" href="#boxen">Zu den Boxen</a>
      <header className="app-header">
        <div className="app-header-inner">
          <div className="app-brand">
            <span className="app-brand-mark" aria-hidden="true">L</span>
            <div>
              <div className="app-brand-kicker">Dachboden</div>
              <h1 className="app-brand-title">Lagerverwaltung</h1>
            </div>
          </div>
          <div className="app-header-actions">
            <button className="app-button app-button-primary" onClick={handleNewBox}>
              <span aria-hidden="true">＋</span><span>Neue Box</span>
            </button>
            <details className="app-tools" onKeyDown={event => {
              if (event.key === 'Escape') {
                event.currentTarget.open = false;
                event.currentTarget.querySelector('summary').focus();
              }
            }}>
              <summary aria-label="Werkzeuge und Einstellungen">Menü <span aria-hidden="true">☰</span></summary>
              <nav className="app-tools-panel" aria-label="Werkzeuge und Einstellungen" onClick={event => {
                if (event.target.closest('button')) event.currentTarget.closest('details').open = false;
              }}>
                <p>Drucken &amp; organisieren</p>
                <button onClick={() => setSingleLabel('')}>Einzeletikett drucken</button>
                <button onClick={() => setShowLabelPrint(true)}>A4-Etiketten drucken</button>
                <button onClick={() => setShowLocationPrint(true)}>Stellfelder markieren</button>
                <button onClick={() => window.open('/api/boxen/export/csv')}>Bestand exportieren</button>
                <p>Mein Zugang</p>
                <button onClick={() => setShowPasskeySettings(true)}>Face ID und Passkeys</button>
                <button onClick={handleLogout}>Abmelden</button>
              </nav>
            </details>
          </div>
        </div>
      </header>

      <div className="app-toolbar-wrap">
        <div className="app-toolbar">
          <label className="search-field">
            <span className="search-symbol" aria-hidden="true">⌕</span>
            <input
              className="search-input"
              placeholder="Box, Inhalt oder Standort suchen"
              value={search}
              onChange={e => setSearch(e.target.value)}
              aria-label="Boxen durchsuchen"
            />
          </label>
          <select className="category-filter" value={filterKat} onChange={e => setFilterKat(e.target.value)} aria-label="Nach Kategorie filtern">
            <option value="">Alle Kategorien</option>
            {kategorien.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
          {(filterKat || search) && (
            <button className="reset-button" onClick={() => { setFilterKat(''); setSearch(''); }}>Zurücksetzen</button>
          )}
        </div>
      </div>

      <main className="app-main" id="boxen">
        {loading && <div className="app-state">Boxen werden geladen…</div>}
        {error && <div className="app-error">{error}</div>}

        {!loading && !error && (
          <>
            {dueReminders.length > 0 && (
              <section className="reminder-overview" aria-labelledby="reminder-heading">
                <div className="reminder-overview-heading">
                  <div>
                    <div className="reminder-overview-kicker">Jetzt prüfen</div>
                    <h2 id="reminder-heading">{dueReminders.length} fällige Erinnerung{dueReminders.length !== 1 ? 'en' : ''}</h2>
                  </div>
                  <span className="reminder-overview-mark" aria-hidden="true">!</span>
                </div>
                <div className="reminder-overview-list">
                  {dueReminders.map(box => (
                    <button key={box.id} className="reminder-overview-item" onClick={() => handleEdit(box)}>
                      <span className="reminder-overview-box">{box.box_nummer}</span>
                      <span className="reminder-overview-copy">
                        <strong>{box.erinnerung_text || 'Box prüfen'}</strong>
                        <small>Fällig am {formatReminderDate(box.erinnerung_am)}</small>
                      </span>
                      <span className="reminder-overview-open">Öffnen</span>
                    </button>
                  ))}
                </div>
              </section>
            )}

            <div className="overview-heading">
              <div>
                <div className="overview-kicker">Alles an seinem Platz</div>
                <h2 className="overview-title">Deine Boxen</h2>
                <p className="overview-description">Finden, was du aufbewahrst.</p>
              </div>
              <div className="overview-stats" aria-label="Bestandsübersicht">
                <span className="stat-pill"><strong>{activeBoxes.length}</strong> befüllt</span>
                {dueReminders.length > 0 && <span className="stat-pill stat-pill-due"><strong>{dueReminders.length}</strong> fällig</span>}
                {preparedBoxes.length > 0 && <span className="stat-pill stat-pill-muted"><strong>{preparedBoxes.length}</strong> vorbereitet</span>}
                {(filterKat || search || showPrepared) && <span className="stat-pill stat-pill-accent"><strong>{filtered.length}</strong> {searchLower || filterKat ? 'Treffer' : 'angezeigt'}</span>}
              </div>
            </div>
            {preparedBoxes.length > 0 && (
              <button className="prepared-toggle" onClick={() => setShowPrepared(value => !value)}>
                {showPrepared ? 'Vorbereitete Boxen ausblenden' : `${preparedBoxes.length} vorbereitete Box${preparedBoxes.length !== 1 ? 'en' : ''} anzeigen`}
              </button>
            )}
            {groups.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-mark" aria-hidden="true">□</div>
                {searchLower || filterKat ? (
                  <>
                    <h3>Keine passenden Boxen gefunden</h3>
                    <p>Ändere den Suchbegriff oder setze die Filter zurück.</p>
                    <button className="app-button app-button-primary" onClick={() => { setSearch(''); setFilterKat(''); }}>
                      Suche und Filter zurücksetzen
                    </button>
                  </>
                ) : (
                  <>
                    <h3>Noch keine befüllten Boxen</h3>
                    <p>Lege die erste Box an und dokumentiere ihren Inhalt direkt mit Fotos.</p>
                    <button className="app-button app-button-primary" onClick={handleNewBox}>
                      Erste Box anlegen
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div className="box-grid">
                {groups.map(entries => (
                  <section key={entries[0].box.id}
                    className={entries.length > 1 ? 'box-family box-family-grouped' : 'box-family'}
                    aria-label={entries.length > 1 ? `Box ${entries[0].box.box_nummer} mit Unterboxen` : `Box ${entries[0].box.box_nummer}`}>
                    {entries.length > 1 && <h3 className="box-family-title">{entries[0].box.box_nummer} mit Unterboxen</h3>}
                    {entries.map(({ box, isSub, parentNumber, contextOnly }) => (
                      <div key={box.id} className="box-family-entry">
                        {contextOnly && <p className="box-family-context">Hauptbox zum Suchtreffer</p>}
                        <BoxKarte box={box} isSubBox={isSub} parentNumber={parentNumber}
                          onEdit={handleEdit} onDeleted={handleDeleted} onPrint={box => setSingleLabel(box.box_nummer)} />
                      </div>
                    ))}
                  </section>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* Formular-Modal */}
      {showForm && (
        <BoxForm
          box={editBox}
          initialBoxNumber={newBoxNumber}
          kategorien={kategorien}
          onSaved={handleSaved}
          onCancel={closeForm}
        />
      )}

      {singleLabel !== null && <SingleLabelPrint initialNumber={singleLabel} onClose={() => setSingleLabel(null)} />}

      {showLabelPrint && (
        <LabelPrint
          onClose={() => setShowLabelPrint(false)}
        />
      )}

      {showLocationPrint && (
        <LocationPrint onClose={() => setShowLocationPrint(false)} />
      )}

      {showPasskeySettings && (
        <PasskeySettings
          onClose={() => setShowPasskeySettings(false)}
          onLogout={handleLogout}
        />
      )}

      {locationAssignment && (
        <LocationAssign
          baseCode={locationAssignment.baseCode}
          initialLevel={locationAssignment.level}
          initialBoxNumber={locationAssignment.boxNumber}
          boxes={boxen}
          onAssigned={handleSaved}
          onClose={closeLocationAssignment}
        />
      )}
    </div>
  );
}
